import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import RIcon from "../../reservas/components/ReservasIcons";
import { formatDuration, formatMoney } from "../beautyBooking";
import {
  buscarVariante,
  idDeAdicional,
  idDeVariante,
  tieneAdicionales,
  tieneVariantes,
  totalDeEleccion,
} from "../../../helpers/beautyLineas";

/**
 * Picker of the options of one service: a variant (radio, required when the service has variants) and optional
 * add-ons (checkbox). Mobile shows it as a bottom sheet, desktop as a centered modal; both share the same body.
 * `initial` = `{ variantId, addOnIds }` of a service already in the cart (edit); `onConfirm` receives the same shape.
 */
const useOpciones = (service, initial) => {
  const [variantId, setVariantId] = useState(() => (buscarVariante(service, initial?.variantId) ? String(initial.variantId) : ""));
  const [addOnIds, setAddOnIds] = useState(() => {
    const validos = (service?.addOns || []).map(idDeAdicional);
    return (initial?.addOnIds || []).filter((id) => validos.includes(id));
  });
  const necesitaVariante = tieneVariantes(service);
  const completa = !necesitaVariante || Boolean(variantId);
  const total = useMemo(() => totalDeEleccion(service, variantId, addOnIds), [service, variantId, addOnIds]);
  const alternar = (id) => setAddOnIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const texto = (verbo) =>
    completa ? `${verbo} · ${formatDuration(total.minutos)} · ${formatMoney(total.precio)}` : "Elegí una opción";
  return { variantId, setVariantId, addOnIds, alternar, completa, texto };
};

const Opciones = ({ service, estado, nombre }) => (
  <>
    {tieneVariantes(service) && (
      <fieldset className="beauty-opt-group">
        <legend className="beauty-group-title">Elegí una opción</legend>
        {service.variants.map((v) => {
          const id = idDeVariante(v);
          return (
            <label key={id} className={`beauty-opt${estado.variantId === id ? " is-added" : ""}`}>
              <input type="radio" name={nombre} checked={estado.variantId === id} onChange={() => estado.setVariantId(id)} />
              <span className="beauty-opt__main">
                <span className="beauty-opt__name">{v.name}</span>
                <span className="beauty-opt__meta">{formatDuration(v.durationMinutes)}</span>
              </span>
              <span className="beauty-opt__price">{formatMoney(v.price)}</span>
            </label>
          );
        })}
      </fieldset>
    )}
    {tieneAdicionales(service) && (
      <fieldset className="beauty-opt-group">
        <legend className="beauty-group-title">Sumá adicionales (opcional)</legend>
        {service.addOns.map((a) => {
          const id = idDeAdicional(a);
          const marcado = estado.addOnIds.includes(id);
          return (
            <label key={id} className={`beauty-opt${marcado ? " is-added" : ""}`}>
              <input type="checkbox" checked={marcado} onChange={() => estado.alternar(id)} />
              <span className="beauty-opt__main">
                <span className="beauty-opt__name">{a.name}</span>
                {Number(a.durationMinutes) > 0 && <span className="beauty-opt__meta">+ {formatDuration(a.durationMinutes)}</span>}
              </span>
              <span className="beauty-opt__price">+ {formatMoney(a.price)}</span>
            </label>
          );
        })}
      </fieldset>
    )}
  </>
);

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

/** Mobile bottom sheet: focus goes in, Tab stays inside, Escape and the scrim close it, focus returns to the opener. */
export const OpcionesSheet = ({ service, initial, onConfirm, onClose }) => {
  const estado = useOpciones(service, initial);
  const titleId = useId();
  const ref = useRef(null);
  const editando = Boolean(initial);

  useEffect(() => {
    const previo = document.activeElement;
    ref.current?.querySelector("input, button")?.focus();
    return () => previo?.focus?.();
  }, []);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      return onClose();
    }
    if (e.key !== "Tab" || !ref.current) return;
    const items = Array.from(ref.current.querySelectorAll(FOCUSABLE));
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <div className="rmob-scrim" onClick={onClose} aria-hidden="true" />
      <div ref={ref} className="rmob-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} onKeyDown={onKeyDown}>
        <span className="rmob-sheet__handle" aria-hidden="true" />
        <div className="rmob-sheet__head">
          <div>
            <h2 id={titleId} className="section-title">{service.name}</h2>
            {service.description && <div className="beauty-service-desc">{service.description}</div>}
          </div>
          <button type="button" className="rmob-back" aria-label="Cerrar" onClick={onClose}>
            <RIcon name="x" width={16} height={16} />
          </button>
        </div>
        <div className="rmob-sheet__body">
          <Opciones service={service} estado={estado} nombre={`variante-${service._id}`} />
        </div>
        <button
          type="button"
          className="r-btn-primary rmob-cta-btn"
          disabled={!estado.completa}
          onClick={() => onConfirm({ variantId: estado.variantId, addOnIds: estado.addOnIds })}
        >
          {estado.texto(editando ? "Guardar" : "Agregar")}
        </button>
      </div>
    </>
  );
};

/** Desktop centered modal with the same styles as the other reservas modals. */
export const OpcionesModal = ({ service, initial, onConfirm, onClose }) => {
  const estado = useOpciones(service, initial);
  const editando = Boolean(initial);
  return (
    <Modal show onHide={onClose} centered className="payment-modal options-modal" aria-labelledby="opciones-title">
      <Modal.Header closeButton>
        <Modal.Title id="opciones-title">{service.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {service.description && <p className="beauty-service-desc">{service.description}</p>}
        <Opciones service={service} estado={estado} nombre={`variante-${service._id}`} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button
          variant="primary"
          disabled={!estado.completa}
          onClick={() => onConfirm({ variantId: estado.variantId, addOnIds: estado.addOnIds })}
        >
          {estado.texto(editando ? "Guardar" : "Agregar")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
